'use client';

import { FC, useEffect, useRef } from 'react';
import { Button, BodySmall } from '@puzzlio/ui';
import { manageUrl, subscriptionCTA, subscriptionMessage } from '@puzzlio/core';
import { cn } from '@/app/lib/utils';
import { trackPaymentFailedClicked, trackPaymentFailedSeen } from '@/app/lib/analytics-events';
import { useUser } from '@/app/providers/UserProvider';
import { useStripeCreateBillingPortalSession, useStripeSubscription } from '@/app/hooks/useStripe';

/*
  Tells a subscriber their payment failed, while they still have access.

  A grace period is invisible otherwise: they keep playing, and then one day they don't. The account
  page says so, but nobody goes there -- so the warning has to find them where they already are.

  Shown only for `past_due`, which is precisely the window in which the subscription still works and
  can still be rescued. It can't be dismissed: it goes away when they fix their billing or cancel,
  either of which ends `past_due`.
*/

export const PaymentIssueBanner: FC<{ className?: string }> = ({ className }) => {
  const user = useUser();
  const { data: subscription, isLoading } = useStripeSubscription();
  const { mutateAsync: createBillingPortalSession } = useStripeCreateBillingPortalSession();

  const pastDue = !isLoading && user && subscription?.status === 'past_due' ? subscription : null;

  // Once per page view, not per re-render.
  const seenTracked = useRef(false);
  useEffect(() => {
    if (!pastDue || seenTracked.current) return;
    seenTracked.current = true;
    trackPaymentFailedSeen(subscriptionCTA(pastDue).target);
  }, [pastDue]);

  if (!pastDue) return null;

  const { label, target } = subscriptionCTA(pastDue);

  const onFix = async () => {
    trackPaymentFailedClicked(target);
    const storeUrl = manageUrl(target);
    if (storeUrl) {
      window.location.href = storeUrl;
      return;
    }
    const { url } = await createBillingPortalSession();
    window.location.href = url;
  };

  return (
    <aside
      role="status"
      aria-label="Payment problem"
      className={cn('w-full border-b border-orange-300 bg-orange-100', className)}
    >
      <div className="mx-auto flex w-full max-w-[740px] flex-col gap-3 px-5 py-3 md:flex-row md:items-center">
        <BodySmall className="flex-1 text-black">{subscriptionMessage(pastDue)}</BodySmall>
        <Button variant="gameplay" size="sm" color="orange" className="shrink-0 self-center" onClick={onFix}>
          {label}
        </Button>
      </div>
    </aside>
  );
};
